import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { Star, CheckCircle2, Phone, ArrowRight } from 'lucide-react';
import { testimonials } from '../data/mock';

const ReviewsPage = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Customer Reviews - Pierce Premier Cleaning Services",
    "description": "Read authentic reviews from satisfied customers in Brevard County, Florida who trust Pierce Premier Cleaning Services",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pierce-premier.preview.emergentagent.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Reviews",
          "item": "https://pierce-premier.preview.emergentagent.com/reviews"
        }
      ]
    }
  };

  return (
    <>
      <SEO 
        title="Customer Reviews | Pierce Premier Cleaning Services | Brevard County"
        description="Read authentic 5-star reviews from Brevard County families and businesses who trust Pierce Premier Cleaning Services for reliable, professional cleaning."
        canonical="/reviews"
        structuredData={structuredData}
      />

      <div className="min-h-screen pt-20 bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-gray-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Customer Reviews
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
              Real reviews from satisfied clients in Brevard County
            </p>

            {/* Thumbtack Widget */}
            <div className="flex justify-center mb-10">
              <div className="widget bg-white border-2 border-gray-200 rounded-xl p-8 shadow-xl" id="tt-review-widget-star">
                <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/logos/thumbtack/wordmark.svg" alt="Thumbtack" className="h-6 mb-4 mx-auto" />
                <a href="https://www.thumbtack.com/fl/palm-bay/house-cleaning/pierce-premier-cleaning-services/service/574132948530225153" className="text-gray-900 font-semibold hover:text-blue-600 transition-colors text-lg" target="_blank" rel="noopener noreferrer">
                  <div>Pierce Premier Cleaning Services</div>
                </a>
                <div id="tt-dynamic" className="flex items-center justify-center gap-2 mt-4">
                  <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                  <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                  <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                  <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                  <img src="https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg" className="w-7 h-7" alt="star" />
                  <span className="text-gray-700 font-semibold ml-2 text-lg">7 reviews</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews Grid */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((review) => (
                <Card key={review.id} className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-center mb-6">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    
                    <div className="mb-4">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                        {review.service}
                      </span>
                    </div>

                    <blockquote className="text-gray-700 leading-relaxed mb-6 italic">
                      "{review.text}"
                    </blockquote>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="font-semibold text-gray-900">{review.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{review.location}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Clients Trust Pierce Premier
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <div className="flex items-start space-x-4 text-left">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Local Ownership</h3>
                  <p className="text-gray-600">Husband-and-wife team personally invested in every job</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 text-left">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Consistent Service</h3>
                  <p className="text-gray-600">Same trusted professionals, not rotating crews</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 text-left">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">15+ Years Experience</h3>
                  <p className="text-gray-600">Executive housekeeping expertise in every cleaning</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 text-left">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Fully Insured</h3>
                  <p className="text-gray-600">Complete protection and peace of mind</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Experience 5-Star Service
            </h2>
            <p className="text-xl mb-10 text-blue-50">
              Join our satisfied clients throughout Brevard County
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-7">
                  Request Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:3214081347">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-7">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 321-408-1347
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ReviewsPage;
